export const EMPTY_MEDICINE_INVENTORY_SUMMARY = {
  totalMedicines: 0,
  totalQuantity: 0,
  inTransactions: 0,
  inQuantity: 0,
  outTransactions: 0,
  outQuantity: 0,
  allTransactions: 0,
  byDepartment: [],
};

const normalizeName = (value) => (value || "").toString().trim().toLowerCase();

export const getMedicineTransactionQuantity = (transaction) => {
  const qty = Number(transaction?.deductedQuantity) || 0;
  const type = String(transaction?.transactionType || "").toUpperCase();
  if (type === "IN" && qty < 0) {
    return Math.abs(qty);
  }
  return Math.abs(qty);
};

const isInboundTransaction = (transactionType) => {
  const type = String(transactionType || "").toUpperCase();
  return type === "IN" || type === "RETURN";
};

const isOutboundTransaction = (transactionType) => {
  return String(transactionType || "").toUpperCase() === "OUT";
};

const ensureCategoryEntry = (categoryMap, category) => {
  const key = category || "Uncategorized";
  if (!categoryMap.has(key)) {
    categoryMap.set(key, {
      department: key,
      medicines: 0,
      totalQuantity: 0,
      inQuantity: 0,
      outQuantity: 0,
      transactions: 0,
    });
  }
  return categoryMap.get(key);
};

export function buildMedicineInventoryExportSummary({
  inventoryRows = [],
  transactions = [],
}) {
  const rows = Array.isArray(inventoryRows) ? inventoryRows : [];
  const txs = Array.isArray(transactions) ? transactions : [];

  if (rows.length === 0 && txs.length === 0) {
    return { ...EMPTY_MEDICINE_INVENTORY_SUMMARY };
  }

  const medicineNameToCategory = new Map();
  const categoryMap = new Map();

  rows.forEach((row) => {
    const category = row?.medicineType || "Uncategorized";
    const nameKey = normalizeName(row?.name);
    if (nameKey) {
      medicineNameToCategory.set(nameKey, category);
    }
    const entry = ensureCategoryEntry(categoryMap, category);
    entry.medicines += 1;
    entry.totalQuantity += Number(row?.totalQuantity) || 0;
  });

  const medicineNames = new Set(
    rows.map((row) => normalizeName(row?.name)).filter(Boolean)
  );
  const relevantTransactions =
    medicineNames.size > 0
      ? txs.filter((tx) => medicineNames.has(normalizeName(tx?.medicineName)))
      : txs;

  let inTransactions = 0;
  let inQuantity = 0;
  let outTransactions = 0;
  let outQuantity = 0;

  relevantTransactions.forEach((tx) => {
    const qty = getMedicineTransactionQuantity(tx);
    const category =
      medicineNameToCategory.get(normalizeName(tx?.medicineName)) || "Uncategorized";
    const entry = ensureCategoryEntry(categoryMap, category);
    entry.transactions += 1;

    if (isInboundTransaction(tx?.transactionType)) {
      inTransactions += 1;
      inQuantity += qty;
      entry.inQuantity += qty;
      return;
    }

    if (isOutboundTransaction(tx?.transactionType)) {
      outTransactions += 1;
      outQuantity += qty;
      entry.outQuantity += qty;
    }
  });

  return {
    totalMedicines: rows.length,
    totalQuantity: rows.reduce(
      (sum, row) => sum + (Number(row?.totalQuantity) || 0),
      0
    ),
    inTransactions,
    inQuantity,
    outTransactions,
    outQuantity,
    allTransactions: relevantTransactions.length,
    byDepartment: Array.from(categoryMap.values()).sort((a, b) =>
      String(a.department).localeCompare(String(b.department))
    ),
  };
}
